import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Avatar,
    Card,
    CardContent, 
    Grid,
    Switch,
    FormControlLabel,
    Snackbar,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    OutlinedInput,
    IconButton,
    useTheme,
    alpha,
    Paper,
    Divider,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Stack,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ListItemIcon,
    FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Edit, Save, Cancel, Person, PhotoCamera } from '@mui/icons-material';
import BookMyOpAPI from '../services/BookMyOpapi';
import { getSpecialties } from '../services/openapis';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import config from '../config';
import BackButton from '../components/BackButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { format } from 'date-fns';

const documentTypeOptions = [
    { value: 'prescription', label: 'Prescription' },
    { value: 'report', label: 'Report' },
    { value: 'scan', label: 'Scan' },
    { value: 'test', label: 'Test' },
    { value: 'other', label: 'Other' }
];

function validateFile(file) {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Only PDF and images are allowed.');
    }
    if (file.size > maxSize) {
        throw new Error('File size exceeds 10MB limit.');
    }
    return true;
}

// Commented out for now - Patient Documents Section
/*
const PatientDocumentsCard = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [documentType, setDocumentType] = useState('');
    const [fileError, setFileError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [viewerDialog, setViewerDialog] = useState({ open: false, document: null });
    const [removingDocumentId, setRemovingDocumentId] = useState(null);
    const [documentName, setDocumentName] = useState('');

    // Fetch documents
    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const data = await BookMyOpAPI.listPatientDocuments();
            setDocuments(data.results || data);
        } catch (error) {
            setFileError(error.message || 'Failed to fetch documents');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    // Upload handler
    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        setFileError('');
        setUploadSuccess(false);
        // Validation: require document_type and name
        if (!documentType) {
            setFileError('Please select a document type.');
            return;
        }
        if (!documentName.trim()) {
            setFileError('Please enter a document name.');
            return;
        }
        try {
            files.forEach(file => validateFile(file));
            setUploading(true);
            const formData = new FormData();
            files.forEach((file) => {
                formData.append('document', file); // API expects 'document'
                formData.append('document_type', documentType);
                formData.append('name', documentName.trim());
            });
            const response = await BookMyOpAPI.uploadAppointmentDocuments(formData);
            setUploadSuccess(true);
            setDocumentType('');
            setDocumentName('');
            fetchDocuments();
        } catch (error) {
            setFileError(error.message || 'Failed to upload document');
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    // View document
    const handleViewDocument = (doc) => {
        setViewerDialog({ open: true, document: doc });
    };
    const handleCloseViewer = () => setViewerDialog({ open: false, document: null });

    // Download document
    const handleDownloadDocument = (doc) => {
        const url = doc.document.startsWith('http') ? doc.document : `${config.backend.host}${doc.document}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = (doc.name || url.split('/').pop());
        link.click();
    };

    // Delete document
    const handleRemoveDocument = async (doc) => {
        setRemovingDocumentId(doc.id);
        try {
            await BookMyOpAPI.removeAppointmentDocument(doc.id);
            setDocuments(prev => prev.filter(d => d.id !== doc.id));
        } catch (error) {
            setFileError(error.message || 'Failed to delete document');
        } finally {
            setRemovingDocumentId(null);
        }
    };

    // Document viewer dialog
    const DocumentViewerDialog = ({ open, onClose, document }) => {
        if (!document || !document.document) return null;
        const isImage = document.document.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        const isPDF = document.document.match(/\.pdf$/i);
        const fullUrl = document.document.startsWith('http') ? document.document : `${config.backend.host}${document.document}`;
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <ListItemIcon>
                            {isPDF ? <PictureAsPdfIcon color="error" /> : isImage ? <ImageIcon color="primary" /> : <DescriptionIcon color="secondary" />}
                        </ListItemIcon>
                        <Box>
                            <Typography variant="h6">{document.name || document.document.split('/').pop()}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {document.document_type} • Uploaded on {format(new Date(document.uploaded_at), 'MMM d, yyyy h:mm a')}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                    {isImage ? (
                        <Box component="img" src={fullUrl} alt={document.name || document.document.split('/').pop()} sx={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#f5f5f5' }} />
                    ) : isPDF ? (
                        <iframe src={fullUrl} style={{ width: '100%', height: '100%', border: 'none' }} title={document.name || document.document.split('/').pop()} />
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
                            <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                            <Typography variant="h6" color="text.secondary">Document Preview Not Available</Typography>
                            <Button variant="contained" startIcon={<OpenInNewIcon />} onClick={() => window.open(fullUrl, '_blank')}>Open Document</Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="inherit">Close</Button>
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => handleDownloadDocument(document)}>Download</Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <Card elevation={3} sx={{ mb: 4 }}>
            <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Patient Documents</Typography>
                {/* Upload New Document *\/}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Document Type</InputLabel>
                            <Select value={documentType} onChange={e => setDocumentType(e.target.value)} label="Document Type">
                                {documentTypeOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={5}>
                        <TextField
                            label="Document Name"
                            value={documentName}
                            onChange={e => setDocumentName(e.target.value)}
                            fullWidth
                            size="small"
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={4}>
                        <Button component="label" variant="outlined" fullWidth disabled={!documentType || !documentName.trim() || uploading} startIcon={uploading ? <CircularProgress size={20} /> : null}>
                            {uploading ? 'Uploading...' : 'Select File'}
                            <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} disabled={uploading} />
                        </Button>
                    </Grid>
                </Grid>
                {fileError && <Alert severity="error" sx={{ mb: 2 }}>{fileError}</Alert>}
                {uploadSuccess && <Alert severity="success" sx={{ mb: 2 }}>Document uploaded successfully!</Alert>}
                <FormHelperText sx={{ mb: 2 }}>Supported file types: PDF, JPG, PNG (Max 10MB per file)</FormHelperText>
                {/* Documents List *\/}
                {loading ? <CircularProgress /> : (
                    documents.length > 0 ? (
                        <List>
                            {documents.map(doc => {
                                if (!doc || !doc.document) return null;
                                const isImage = doc.document && doc.document.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                const isPDF = doc.document && doc.document.match(/\.pdf$/i);
                                return (
                                    <ListItem key={doc.id} sx={{ border: '1px solid rgba(0, 172, 193, 0.1)', borderRadius: 1, mb: 1, '&:hover': { backgroundColor: 'primary.main', opacity: 0.05 } }} secondaryAction={
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="View Document"><IconButton size="small" onClick={() => handleViewDocument(doc)} sx={{ color: 'primary.main', border: '1px solid', borderColor: 'primary.main' }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Download"><IconButton size="small" onClick={() => handleDownloadDocument(doc)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleRemoveDocument(doc)} disabled={removingDocumentId === doc.id}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                        </Stack>
                                    }>
                                        <ListItemIcon>{isPDF ? <PictureAsPdfIcon color="error" /> : isImage ? <ImageIcon color="primary" /> : <DescriptionIcon color="secondary" />}</ListItemIcon>
                                        <ListItemText 
                                        sx={{fontSize:'9px'}}
                                            primary={doc.name || doc.document.split('/').pop()} 
                                            secondary={
                                                <>
                                                    {doc.document_type}
                                                    <br />
                                                    Uploaded on {format(new Date(doc.uploaded_at), 'MMM d, yyyy h:mm a')}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    ) : <Typography color="text.secondary">No documents uploaded yet.</Typography>
                )}
                {/* Viewer Dialog *\/}
                <DocumentViewerDialog open={viewerDialog.open} onClose={handleCloseViewer} document={viewerDialog.document} />
            </CardContent>
        </Card>
    );
};
*/

// Styled components for better visual design
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    // boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    // transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-2px)',
        // boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    width: 80,
    height: 80,
    border: `4px solid ${theme.palette.background.paper}`,
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
        transform: 'scale(1.02)',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.5, 3),
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
}));

const ProfileSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .section-title': {
        fontSize: '1.1rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    },
    '& .field-item': {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1.5, 0),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    '& .field-label': {
        fontWeight: 600,
        color: theme.palette.text.primary,
        minWidth: '140px',
    },
    '& .field-value': {
        color: theme.palette.text.secondary,
        textAlign: 'right',
        flex: 1,
    },
}));

const Profile = () => {
    const { refreshProfile } = useBookMyOpAuth();
    const theme = useTheme();
    
    // State
    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [specialties, setSpecialties] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Profile data
    const [profile, setProfile] = useState({
        id: null,
        email: '',
        name: '',
        mobile_number: '',
        date_of_birth: '',
        gender: '',
        address: '',
        profile_photo: null,
        preferred_specialties: [],
        has_medical_insurance: false,
        insurance_details: null
    });
    
    // Form data (separate from profile to avoid re-rendering issues)
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        mobile_number: '',
        date_of_birth: '',
        gender: '',
        address: '',
        preferred_specialties: [],
        has_medical_insurance: false
    });
    
    const [profilePhotoFile, setProfilePhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    // Fetch profile data
    const fetchProfile = async () => {
        try {
            setIsLoading(true);
            const response = await BookMyOpAPI.getProfile();
            const profileData = response.data;
            
            if (profileData) {
                const newProfile = {
                    id: profileData.id || null,
                    email: profileData.email || '',
                    name: profileData.name || '',
                    mobile_number: profileData.mobile_number || '',
                    date_of_birth: profileData.date_of_birth || '',
                    gender: profileData.gender || '',
                    address: profileData.address || '',
                    profile_photo: profileData.profile_photo || null,
                    preferred_specialties: Array.isArray(profileData.preferred_specialties) ? profileData.preferred_specialties : [],
                    has_medical_insurance: Boolean(profileData.has_medical_insurance),
                    insurance_details: profileData.insurance_details || null
                };
                
                setProfile(newProfile);
                setFormData({
                    email: newProfile.email,
                    name: newProfile.name,
                    mobile_number: newProfile.mobile_number,
                    date_of_birth: newProfile.date_of_birth,
                    gender: newProfile.gender,
                    address: newProfile.address,
                    preferred_specialties: [...newProfile.preferred_specialties],
                    has_medical_insurance: newProfile.has_medical_insurance
                });
                
                if (profileData.profile_photo) {
                    setPhotoPreview(profileData.profile_photo);
                }
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            showSnackbar('Error fetching profile', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch specialties
    const fetchSpecialties = async () => {
        try {
            const response = await getSpecialties();
            if (response && response.results) {
                setSpecialties(response.results);
            }
        } catch (error) {
            console.error('Error fetching specialties:', error);
        }
    };

    useEffect(() => {
        fetchProfile();
        fetchSpecialties();
    }, []);

    // Helper functions
    const getFullImageUrl = (photoUrl) => {
        if (!photoUrl) return null;
        if (photoUrl.startsWith('http')) return photoUrl;
        return `${config.backend.host}${photoUrl}`;
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        
        if (name === 'profile_photo' && files?.[0]) {
            setProfilePhotoFile(files[0]);
            const previewUrl = URL.createObjectURL(files[0]);
            setPhotoPreview(previewUrl);
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSpecialtyChange = (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            preferred_specialties: typeof value === 'string' ? value.split(',').map(Number) : value
        }));
    };

    const handleDeleteSpecialty = (specialtyId) => {
        setFormData(prev => ({
            ...prev,
            preferred_specialties: prev.preferred_specialties.filter(id => id !== specialtyId)
        }));
    };

    // Action handlers
    const handleEdit = () => {
        setIsEditMode(true);
        // Reset form data to current profile
        setFormData({
            email: profile.email,
            name: profile.name,
            mobile_number: profile.mobile_number,
            date_of_birth: profile.date_of_birth,
            gender: profile.gender,
            address: profile.address,
            preferred_specialties: [...profile.preferred_specialties],
            has_medical_insurance: profile.has_medical_insurance
        });
        setProfilePhotoFile(null);
        setPhotoPreview(profile.profile_photo);
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setProfilePhotoFile(null);
        setPhotoPreview(profile.profile_photo);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setIsLoading(true);
            
            // Only include changed fields
            const changedFields = {};
            
            Object.keys(formData).forEach(key => {
                if (key === 'preferred_specialties') {
                    // Compare arrays
                    const original = profile.preferred_specialties || [];
                    const updated = formData.preferred_specialties || [];
                    if (JSON.stringify(original.sort()) !== JSON.stringify(updated.sort())) {
                        changedFields[key] = updated;
                    }
                } else {
                    // Compare other fields
                    if (formData[key] !== profile[key]) {
                        changedFields[key] = formData[key];
                    }
                }
            });

            // If no fields changed and no photo, don't submit
            if (Object.keys(changedFields).length === 0 && !profilePhotoFile) {
                showSnackbar('No changes to save', 'info');
                setIsEditMode(false);
                return;
            }

            // Always use FormData as backend expects multipart/form-data
            const formDataObj = new FormData();
            
            // Add profile photo if changed
            if (profilePhotoFile) {
                formDataObj.append('profile_photo', profilePhotoFile);
            }
            
            // Only append changed fields
            Object.entries(changedFields).forEach(([key, value]) => {
                if (key === 'preferred_specialties' && value?.length > 0) {
                    value.forEach((id, index) => {
                        formDataObj.append(`preferred_specialties[${index}]`, id);
                    });
                } else if (value !== null && value !== '') {
                    formDataObj.append(key, value.toString());
                }
            });

            await BookMyOpAPI.updateProfile(formDataObj, { 'Content-Type': 'multipart/form-data' });
            await refreshProfile();
            await fetchProfile();
            
            setIsEditMode(false);
            showSnackbar('Profile updated successfully');
        } catch (error) {
            console.error('Error updating profile:', error);
            showSnackbar(error.response?.data?.message || 'Error updating profile', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Render functions
    const renderProfileView = () => (
        <StyledCard elevation={3} sx={{boxShadow:'none !important',bgcolor:'transparent'}}>
            <CardContent sx={{ }}>
           
                {/* Header with Edit Button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StyledAvatar
                            src={getFullImageUrl(profile.profile_photo)}
                            alt={profile.name}
                            sx={{ 
                                bgcolor: theme.palette.primary.main,
                                mr: 3,
                                width: 60,
                                height: 60
                            }}
                        >
                            {!profile.profile_photo && <Person sx={{ fontSize: 40 }} />}
                        </StyledAvatar>
                        
                        <Box>
                            <Typography 
                                variant="h4" 
                                gutterBottom 
                                sx={{ 
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    mb: 0.5
                                }}
                            >
                                {profile.name || 'No Name'}
                            </Typography>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    fontSize: '1.1rem'
                                }}
                            >
                                {profile.email || 'No Email'}
                            </Typography>
                        </Box>
                        
                    </Box>
                    {/* Edit Button at right edge */}
                    <Tooltip title="Edit Profile">
                        <span>
                            <IconButton
                                color="primary"
                                onClick={handleEdit}
                                aria-label="edit profile"
                                sx={{ ml: 2 }}
                            >
                                <Edit />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                {/* <Divider sx={{ mb: 4 }} /> */}

                {/* Profile Details */}
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <ProfileSection>
                            <Typography className="section-title">
                                Contact Information
                            </Typography>
                            {/* <Box className="field-item">
                                <Typography className="field-label">Mobile:</Typography>
                                <Typography className="field-value">
                                    {profile.mobile_number || 'Not provided'}
                                </Typography>
                            </Box> */}
                            <Box className="field-item">
                                <Typography className="field-label">Date of Birth:</Typography>
                                <Typography className="field-value">
                                    {profile.date_of_birth || 'Not provided'}
                                </Typography>
                            </Box>
                            <Box className="field-item">
                                <Typography className="field-label">Gender:</Typography>
                                <Typography className="field-value">
                                    {profile.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : 'Not provided'}
                                </Typography>
                            </Box>
                            <Box className="field-item">
                                <Typography className="field-label">Address:</Typography>
                                <Typography className="field-value">
                                    {profile.address || 'Not provided'}
                                </Typography>
                            </Box>
                        </ProfileSection>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ProfileSection>
                            <Typography className="section-title">
                                Medical Preferences
                            </Typography>
                            <Box className="field-item">
                                <Typography className="field-label">Medical Insurance:</Typography>
                                <Typography className="field-value">
                                    {profile.has_medical_insurance ? 'Yes' : 'No'}
                                </Typography>
                            </Box>
                            {/* <Box className="field-item">
                                <Typography className="field-label">Preferred Specialties:</Typography>
                                <Box className="field-value" sx={{ textAlign: 'left' }}>
                                    {profile.preferred_specialties && profile.preferred_specialties.length > 0 
                                        ? (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {profile.preferred_specialties.map(id => {
                                                    const specialty = specialties.find(s => s.id === id);
                                                    return specialty ? (
                                                        <Chip 
                                                            key={id} 
                                                            label={specialty.name}
                                                            size="small"
                                                            sx={{ 
                                                                background: alpha(theme.palette.primary.main, 0.1),
                                                                color: theme.palette.primary.main,
                                                                fontWeight: 500
                                                            }}
                                                        />
                                                    ) : null;
                                                }).filter(Boolean)}
                                            </Box>
                                        )
                                        : <Typography variant="body2" color="text.secondary">None selected</Typography>
                                    }
                                </Box>
                            </Box> */}
                        </ProfileSection>
                    </Grid>
                </Grid>
            </CardContent>
        </StyledCard>
    );

    const renderEditForm = () => (
        <StyledCard elevation={3} sx={{boxShadow:'none !important',bgcolor:'transparent'}}>
            <CardContent sx={{ p: 4 }}>
                <Box component="form" onSubmit={handleSubmit}>
                    {/* Profile Photo */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                            <StyledAvatar
                                src={photoPreview ? getFullImageUrl(photoPreview) : getFullImageUrl(profile.profile_photo)}
                                alt={formData.name}
                                sx={{ 
                                    bgcolor: theme.palette.primary.main 
                                }}
                            >
                                {!photoPreview && !profile.profile_photo && <Person sx={{ fontSize: 60 }} />}
                            </StyledAvatar>
                            <IconButton
                                component="label"
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    border: `3px solid ${theme.palette.background.paper}`,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    }
                                }}
                            >
                                <PhotoCamera />
                                <input
                                    type="file"
                                    name="profile_photo"
                                    accept="image/*"
                                    onChange={handleInputChange}
                                    style={{ display: 'none' }}
                                />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Basic Information */}
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3, color: theme.palette.text.primary }}>
                        Basic Information
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                label="Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 4 }} />

                    {/* Contact Information */}
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3, color: theme.palette.text.primary }}>
                        Contact Information
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* <Grid item xs={12} md={6}>
                            <TextField
                                required
                                label="Mobile Number"
                                name="mobile_number"
                                value={formData.mobile_number}
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                inputProps={{ maxLength: 10 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid> */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                type="date"
                                label="Date of Birth"
                                name="date_of_birth"
                                value={formData.date_of_birth}
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleInputChange}
                                    label="Gender"
                                    sx={{
                                        borderRadius: 2,
                                    }}
                                >
                                    <MenuItem value="">Select Gender</MenuItem>
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Address"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                multiline
                                rows={3}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 4 }} />

                    {/* Medical Preferences */}
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3, color: theme.palette.text.primary }}>
                        Medical Preferences
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Preferred Specialties</InputLabel>
                                <Select
                                    multiple
                                    name="preferred_specialties"
                                    value={formData.preferred_specialties}
                                    onChange={handleSpecialtyChange}
                                    input={<OutlinedInput label="Preferred Specialties" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((specialtyId) => {
                                                const specialty = specialties.find(s => s.id === specialtyId);
                                                return (
                                                    <Chip 
                                                        key={specialtyId} 
                                                        label={specialty ? specialty.name : ''}
                                                        onDelete={() => handleDeleteSpecialty(specialtyId)}
                                                        size="small"
                                                        sx={{
                                                            background: alpha(theme.palette.primary.main, 0.1),
                                                            color: theme.palette.primary.main,
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                    sx={{
                                        borderRadius: 2,
                                    }}
                                >
                                    {specialties.map((specialty) => (
                                        <MenuItem key={specialty.id} value={specialty.id}>
                                            {specialty.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid> */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="has_medical_insurance"
                                        checked={formData.has_medical_insurance}
                                        onChange={handleInputChange}
                                        color="primary"
                                    />
                                }
                                label="I have medical insurance"
                                sx={{ 
                                    '& .MuiFormControlLabel-label': {
                                        fontWeight: 500,
                                        fontSize: '1rem'
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 4 }}>
                        <StyledButton
                            onClick={handleCancel}
                            variant="outlined"
                            color="primary"
                            startIcon={<Cancel />}
                            sx={{ 
                                minWidth: 150,
                                borderWidth: 2,
                                '&:hover': {
                                    borderWidth: 2,
                                }
                            }}
                        >
                            Cancel
                        </StyledButton>
                        <StyledButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            startIcon={<Save />}
                            disabled={isLoading}
                            sx={{ 
                                minWidth: 150,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                            }}
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </StyledButton>
                    </Box>
                </Box>
            </CardContent>
        </StyledCard>
    );

    return (
        <Container maxWidth="lg" sx={{  mb: 2, bgcolor:theme.palette.background.paper }}>
            <Box sx={{ display: 'flex', alignItems: 'center', }}>
                <BackButton sx={{ mr: 2 }} />
                <Typography 
                    variant="h2" 
                    sx={{ 
                        fontWeight: 700,
                        flex: 1,
                        textAlign: 'flex-start',
                        background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    //   textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                >
                    Profile 
                </Typography>
             
            </Box>

            {isEditMode ? renderEditForm() : renderProfileView()}

            {/* Patient Documents Section - Commented out for now */}
            {/* <PatientDocumentsCard /> */}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%', borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Profile;
